import React from 'react';
import { Receipt, FileText, Info, Download } from 'lucide-react';

const GSTDetails = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-indigo-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Receipt size={48} />
            <h1 className="text-4xl md:text-5xl font-bold">GST Information</h1>
          </div>
          <p className="text-xl text-indigo-100">Goods and Services Tax details for AV Mart Shamli</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Company GST Details */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Company GST Details</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-l-4 border-indigo-600 pl-4">
              <p className="text-sm text-gray-500 mb-1">Legal Entity Name</p>
              <p className="text-lg font-semibold text-gray-800">AV Mart Shamli Private Limited</p>
            </div>
            <div className="border-l-4 border-indigo-600 pl-4">
              <p className="text-sm text-gray-500 mb-1">GSTIN</p>
              <p className="text-lg font-semibold text-gray-800">09AKDPG1880D1ZF</p>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Registered Office Address</p>
            <p className="text-gray-800">
              Near Jain Vihar, Majra Road<br />
              Shamli - 247776<br />
              India
            </p>
          </div>
        </section>

        {/* GST Rates */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="text-indigo-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">GST Rates on Products</h2>
          </div>
          
          <p className="text-gray-700 mb-6">
            GST is applicable on most products sold on AV Mart Shamli. The rates vary based on product categories 
            as per Indian GST regulations.
          </p>

          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-gray-800">Essential Food Items</h3>
                <span className="bg-green-600 text-white px-4 py-2 rounded-full font-bold">0% - 5% GST</span>
              </div>
              <p className="text-gray-700 mb-2">Products in this category:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Fresh fruits and vegetables (0% GST - Exempted)</li>
                <li>Milk and dairy products (0% GST - Exempted)</li>
                <li>Rice, wheat, flour, atta (0% GST - Exempted)</li>
                <li>Fresh meat, fish, chicken (0% GST - Exempted)</li>
                <li>Eggs (0% GST - Exempted)</li>
                <li>Bread, butter (Specified items may have 5% GST)</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-gray-800">Processed & Packaged Foods</h3>
                <span className="bg-yellow-600 text-white px-4 py-2 rounded-full font-bold">12% GST</span>
              </div>
              <p className="text-gray-700 mb-2">Products in this category:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Packaged food items (except branded items above 12%)</li>
                <li>Processed food products</li>
                <li>Certain dairy products (packaged)</li>
                <li>Fruit juices (packaged)</li>
                <li>Frozen foods</li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-gray-800">Standard Rate Products</h3>
                <span className="bg-orange-600 text-white px-4 py-2 rounded-full font-bold">18% GST</span>
              </div>
              <p className="text-gray-700 mb-2">Products in this category:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Packaged snacks and namkeen</li>
                <li>Biscuits, cookies, cakes</li>
                <li>Chocolates and confectionery</li>
                <li>Ice cream</li>
                <li>Sauces, ketchup, pickles</li>
                <li>Tea, coffee (branded & packaged)</li>
                <li>Spices (branded & packaged)</li>
                <li>Personal care products</li>
                <li>Household cleaning products</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-gray-800">Higher Rate Products</h3>
                <span className="bg-red-600 text-white px-4 py-2 rounded-full font-bold">28% GST</span>
              </div>
              <p className="text-gray-700 mb-2">Products in this category:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Aerated beverages (soft drinks)</li>
                <li>Energy drinks</li>
                <li>Pan masala</li>
                <li>Certain luxury items</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Info className="text-blue-600 flex-shrink-0" size={24} />
              <div>
                <p className="text-gray-800 font-semibold mb-1">Important Note:</p>
                <p className="text-gray-700 text-sm">
                  GST rates are subject to change as per Government of India notifications. The rates 
                  mentioned above are indicative and may vary. Actual GST will be displayed on each 
                  product page and in your invoice.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* GST Invoice */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">GST Invoice</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Invoice Generation</h3>
              <p className="text-gray-700 leading-relaxed">
                A GST-compliant invoice is automatically generated for every order placed on AV Mart Shamli. 
                The invoice includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3 ml-4">
                <li>Invoice number and date</li>
                <li>Your billing details and GSTIN (if provided)</li>
                <li>AV Mart Shamli's GSTIN and address</li>
                <li>Itemized list of products with HSN/SAC codes</li>
                <li>Applicable GST rates (CGST, SGST, IGST)</li>
                <li>Total amount including taxes</li>
              </ul>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <Download className="text-indigo-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Download Your Invoice</h3>
                  <p className="text-gray-700 mb-3">
                    You can download your GST invoice from:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Order confirmation email</li>
                    <li>Your account → Orders → View Invoice</li>
                    <li>Mobile app → My Orders → Download Invoice</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Input Tax Credit */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Input Tax Credit (ITC)</h2>
          
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              If you are a registered business under GST, you can claim Input Tax Credit (ITC) for purchases 
              made from AV Mart Shamli for business purposes.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">To Claim ITC:</h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                <li>Add your company's GSTIN in your AV Mart Shamli account settings</li>
                <li>Ensure your billing address matches your GST registration address</li>
                <li>Download GST invoices for all business purchases</li>
                <li>Use these invoices for filing GST returns and claiming ITC</li>
              </ol>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> ITC is available only on products purchased for business purposes 
                and not for personal consumption. Please consult your tax advisor for specific ITC eligibility.
              </p>
            </div>
          </div>
        </section>

        {/* Business Purchases */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Business Purchases</h2>
          
          <p className="text-gray-700 mb-4">
            AV Mart Shamli welcomes business customers for bulk orders and regular supplies:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Add GST Details</h3>
              <p className="text-gray-700 text-sm mb-3">
                Navigate to Account Settings → Business Information → Add GSTIN
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Valid GSTIN required</li>
                <li>✓ Business name as per GST</li>
                <li>✓ Registered address</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Benefits</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✓ GST-compliant invoices</li>
                <li>✓ Claim Input Tax Credit</li>
                <li>✓ Dedicated support for bulk orders</li>
                <li>✓ Corporate discounts available</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Q: Is GST included in the product prices shown?
              </h3>
              <p className="text-gray-700">
                A: Yes, all product prices displayed on AV Mart Shamli are inclusive of applicable GST. 
                The breakup of GST is shown in your cart and invoice.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Q: Do I need to provide GSTIN for personal purchases?
              </h3>
              <p className="text-gray-700">
                A: No, GSTIN is optional for personal purchases. You only need to provide GSTIN if you 
                want to claim Input Tax Credit for business purposes.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Q: Can I get a GST invoice for past orders?
              </h3>
              <p className="text-gray-700">
                A: Yes, you can download GST invoices for all your past orders from your Order History 
                in your account section.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Q: What if the GST rate changes after I place an order?
              </h3>
              <p className="text-gray-700">
                A: The GST rate applicable at the time of order placement will be charged. Rate changes 
                after order confirmation will not affect your order.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Q: How do I update my GSTIN in my account?
              </h3>
              <p className="text-gray-700">
                A: Go to Account Settings → Profile → Business Information → Enter/Update GSTIN. 
                Make sure to verify it before saving.
              </p>
            </div>
          </div>
        </section>

        {/* Contact for GST Queries */}
        <section className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg shadow-md p-8">
          <h2 className="text-3xl font-bold mb-4">Need Help with GST?</h2>
          <p className="mb-6">
            For any queries related to GST, invoices, or business purchases, please contact our support team:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="font-semibold mb-2">General Support</p>
              <p>Email: support@AV Mart Shamli.com</p>
              <p>Phone: (+91)-7251972000</p>
            </div>
            <div>
              <p className="font-semibold mb-2">Business & GST Queries</p>
              <p>Email: contactus@avmartshamli.com</p>
              <p>Phone: (+91)-7251972000</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-indigo-400">
            <p className="text-sm text-indigo-100">
              <strong>Disclaimer:</strong> The information provided here is for general guidance only. 
              For specific tax advice, please consult a qualified tax professional or chartered accountant.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GSTDetails;
